import { useEffect, useId, useRef } from 'react'
import type { JSX, ReactNode } from 'react'

/** Everything inside a panel that can take focus. */
const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'

/**
 * A modal dialog: focus trap, Escape to close, focus restored on the way out.
 *
 * The React equivalent of `Atlas.modal` in design/atlas/components.js — the
 * behaviour S-D1 designed, reimplemented rather than imported because that file
 * drives the static prototypes through the DOM and would fight React for
 * ownership of the same nodes.
 *
 * All three behaviours are the ones a hand-rolled dialog reliably omits, and
 * each has a concrete failure: without the trap, Tab walks into the page behind
 * and the user types into fields they cannot see; without Escape, a dialog on a
 * phone can be unclosable if the footer scrolls off; without restore, the caret
 * lands at the top of the document and a keyboard user starts again.
 *
 * `dismissable` is false for the session-expiry dialog. Everything else can be
 * escaped.
 */
export function Modal({
  title,
  onClose,
  dismissable = true,
  children,
  footer,
}: {
  title: string
  onClose: () => void
  dismissable?: boolean
  children: ReactNode
  footer?: ReactNode
}): JSX.Element {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useEffect(() => {
    const previous = document.activeElement
    const node = panel.current
    node?.querySelector<HTMLElement>(FOCUSABLE)?.focus()

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape' && dismissable) {
        onClose()
        return
      }
      if (event.key !== 'Tab' || !node) return

      const focusable = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)]
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return

      // Wrap in both directions. Reading the list on each Tab rather than once
      // on open, because a dialog's contents change — a validation message adds
      // a link, a pending button becomes disabled — and a stale list traps
      // focus on an element that is no longer there.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (previous instanceof HTMLElement) previous.focus()
    }
  }, [onClose, dismissable])

  return (
    <div
      className="ar-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onMouseDown={(event) => {
        // The backdrop, not the panel. `mousedown` rather than `click` so a
        // drag that starts inside the panel and ends on the backdrop — selecting
        // text to the edge — does not dismiss the dialog.
        if (dismissable && event.target === event.currentTarget) onClose()
      }}
    >
      <div className="ar-modal__panel" ref={panel}>
        <div className="ar-modal__header">
          <h2 id={titleId}>{title}</h2>
        </div>
        <div className="ar-modal__body">{children}</div>
        {footer ? <div className="ar-modal__footer">{footer}</div> : null}
      </div>
    </div>
  )
}
